import React from "react";
import { Pagination as BootstrapPagination } from "react-bootstrap";
import { createUltimatePagination, ITEM_TYPES } from "react-ultimate-pagination";
import styled, { css } from "styled-components";
import { MoreActionsIcon } from "components/common/MoreActions";
import Icon from "./Icon";
const StyledBootstrapPagination = styled(BootstrapPagination)(
  ({ theme }) => css`
    &.pagination {
      font-size: ${theme.fonts.size.small};
      margin-top: 10px;
      margin-bottom: 0;

      > li {
        > a,
        > span {
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${theme.utils.contrastingColor(theme.colors.global.contentBackground)};
          background-color: ${theme.colors.global.contentBackground};
          border-color: ${theme.colors.variant.light.default};
          border-radius: 0;
          height: 32px;

          &:hover,
          &:focus {
            color: ${theme.utils.contrastingColor(theme.colors.variant.lighter.default)};
            background-color: ${theme.colors.variant.lighter.default};
            border-color: ${theme.colors.variant.light.default};
          }
        }

        &.active > a,
        &.active > span {
          &,
          &:hover,
          &:focus {
            color: ${theme.colors.pagination.active.color};
            background-color: ${theme.colors.pagination.active.background};
            border-color: ${theme.colors.pagination.active.border};
            z-index: 1;
          }
        }

        &.disabled {
          > a,
          > a:hover,
          > a:focus,
          > span,
          > span:hover,
          > span:focus {
            color: ${theme.colors.variant.light.default};
            background-color: ${theme.colors.global.contentBackground};
            border-color: ${theme.colors.variant.lighter.default};
          }
        }
      }
    }
  `
);
const UltimatePagination = createUltimatePagination({
  WrapperComponent: StyledBootstrapPagination,
  itemTypeToComponent: {
    [ITEM_TYPES.PAGE]: ({ value, isActive, onClick }) => {
      const title = isActive ? "Active page" : `Open page ${value}`;
      return /* @__PURE__ */ React.createElement(BootstrapPagination.Item, { active: isActive, onClick, title, "aria-label": title }, value);
    },
    [ITEM_TYPES.ELLIPSIS]: ({ isActive, onClick }) => {
      const title = "Open following page";
      return /* @__PURE__ */ React.createElement(
        BootstrapPagination.Ellipsis,
        {
          disabled: isActive,
          onClick,
          title,
          "aria-label": title,
          className: "pagination-control"
        },
        /* @__PURE__ */ React.createElement(MoreActionsIcon, null)
      );
    },
    [ITEM_TYPES.FIRST_PAGE_LINK]: ({ isActive, onClick }) => {
      const title = "Open first page";
      return /* @__PURE__ */ React.createElement(
        BootstrapPagination.First,
        {
          disabled: isActive,
          onClick,
          title,
          "aria-label": title,
          className: "pagination-control"
        },
        /* @__PURE__ */ React.createElement(Icon, { name: "keyboard_double_arrow_left" })
      );
    },
    [ITEM_TYPES.PREVIOUS_PAGE_LINK]: ({ isActive, onClick }) => {
      const title = "Open previous page";
      return /* @__PURE__ */ React.createElement(
        BootstrapPagination.Prev,
        {
          disabled: isActive,
          onClick,
          title,
          "aria-label": title,
          className: "pagination-control"
        },
        /* @__PURE__ */ React.createElement(Icon, { name: "chevron_left" })
      );
    },
    [ITEM_TYPES.NEXT_PAGE_LINK]: ({ isActive, onClick }) => {
      const title = "Open next page";
      return /* @__PURE__ */ React.createElement(
        BootstrapPagination.Next,
        {
          disabled: isActive,
          onClick,
          title,
          "aria-label": title,
          className: "pagination-control"
        },
        /* @__PURE__ */ React.createElement(Icon, { name: "chevron_right" })
      );
    },
    [ITEM_TYPES.LAST_PAGE_LINK]: ({ isActive, onClick }) => {
      const title = "Open last page";
      return /* @__PURE__ */ React.createElement(
        BootstrapPagination.Last,
        {
          disabled: isActive,
          onClick,
          title,
          "aria-label": title,
          className: "pagination-control"
        },
        /* @__PURE__ */ React.createElement(Icon, { name: "keyboard_double_arrow_right" })
      );
    }
  }
});
const Pagination = ({
  currentPage,
  totalPages,
  boundaryPagesRange = 1,
  siblingPagesRange = 1,
  hideEllipsis = false,
  hidePreviousAndNextPageLinks = false,
  hideFirstAndLastPageLinks = false,
  disabled = false,
  onChange = () => {
  },
  warnIfPageOutOfBounds = true
}) => {
  if (totalPages <= 1) {
    return null;
  }
  if (currentPage > totalPages) {
    if (warnIfPageOutOfBounds) {
      console.warn("Pagination: `currentPage` prop should not be larger than `totalPages` prop.");
    }
    return null;
  }
  return /* @__PURE__ */ React.createElement(
    UltimatePagination,
    {
      currentPage,
      totalPages,
      boundaryPagesRange,
      siblingPagesRange,
      hideEllipsis,
      hidePreviousAndNextPageLinks,
      hideFirstAndLastPageLinks,
      disabled,
      onChange,
      "data-testid": "graylog-pagination"
    }
  );
};
export default Pagination;
